import React from 'react';
import { flushSync } from 'react-dom';
import { get } from 'lodash-es';
import { getStringWithoutSpecialCharacters } from '@/util/helper/dataParser';
import { useApiLoadingPool, usePreventInfiniteLoading, useRouter, useScrollToTop, } from '@/util/hook/custom';
import useCallback from '@/util/hook/useCallback';
import useDispatch from '@/util/hook/useDispatch';
import useEffect from '@/util/hook/useEffect';
import useMemo from '@/util/hook/useMemo';
import useSelector from '@/util/hook/useSelector';
import useState from '@/util/hook/useState';
import { handleSkipCurrentPage } from '../../util/helper/handleSkipCurrentPage';
import { getHospitalOptionList } from '../../util/helper/option';
import { useGetCareClientVisitorProfile } from '../../util/hook/useGetCareClientVisitorProfile';
import { useGetRemoteConfig } from '../../util/hook/useGetRemoteConfig';
import { usePageHeight } from '../../util/hook/usePageHeight';
import { useTrackBrowserHistory } from '../../util/hook/useTrackBrowserHistory';
import { useValidateCareClientVisitorProfileAndRedirect } from '../../util/hook/useValidateCareClientVisitorProfileAndRedirect';
import { careOrderBabyFormUpdate, selectCareOrderBabyForm, } from '../../redux/module/careOrderBabyForm';
import { selectHospitalList } from '../../redux/module/remoteConfig';
import View from './View';
import { APP } from '../../config/app';
import { HOSPITAL_COUNT_PER_PAGE, HOSPITAL_SEARCH_MAX_RESULT, HOSPITAL_SEARCH_PATTERN_SCORE, } from '../../config/search';
const getIsHospitalSelected = ({ hospital, selectedHospital, }) => get(selectedHospital, 'name') === hospital.name &&
    get(selectedHospital, 'city') === hospital.city &&
    get(selectedHospital, 'district') === hospital.district;
const searchHospital = ({ queryText, setSelectedHospital, setSearchResultList, hospitalList, }) => {
    React.startTransition(() => {
        if (!queryText) {
            setSelectedHospital(null);
            return;
        }
        const queryTermList = queryText.replace(/台/g, '臺').split(' ').filter(Boolean);
        const scoredResultList = hospitalList.map((hospital) => {
            let matchScore = 0;
            const hospitalName = get(hospital, 'name', '');
            const hospitalCity = get(hospital, 'city', '');
            queryTermList.forEach((queryTerm) => {
                // matching pattern 1: hospital name inclusive match
                // e.g., '臺大醫' matches '臺大醫院總院區'
                if (hospitalName.includes(queryTerm))
                    matchScore += HOSPITAL_SEARCH_PATTERN_SCORE.NAME_INCLUSIVE_MATCH;
                // matching pattern 2: city inclusive match
                // e.g., '新竹' matches '天主教仁慈醫院(新竹縣湖口鄉...)'
                if (hospitalCity.includes(queryTerm))
                    matchScore += HOSPITAL_SEARCH_PATTERN_SCORE.CITY_INCLUSIVE_MATCH;
                // matching pattern 3: hospital name fuzzy match
                // e.g., '臺大竹' matches '臺大醫院-新竹分院'
                const wordList = queryTerm.split('');
                const regPattern = wordList.join('.*');
                const regExp = new RegExp(regPattern, 'g');
                if (regExp.test(hospitalName))
                    matchScore += HOSPITAL_SEARCH_PATTERN_SCORE.CITY_FUSSY_MATCH;
            });
            return { hospital, matchScore };
        });
        // Always keep those matching with pattern 1 in the list
        let filteredScoredResultList = scoredResultList.filter(({ matchScore }) => matchScore >= HOSPITAL_SEARCH_PATTERN_SCORE.NAME_INCLUSIVE_MATCH);
        // Add other candidates into the list and and trim the tail if the list exceeds the maximum
        filteredScoredResultList = filteredScoredResultList
            .concat(scoredResultList.filter(({ matchScore }) => matchScore < HOSPITAL_SEARCH_PATTERN_SCORE.NAME_INCLUSIVE_MATCH && matchScore > 0))
            .slice(0, HOSPITAL_SEARCH_MAX_RESULT);
        // Sort the list by scores
        filteredScoredResultList.sort((a, b) => b.matchScore - a.matchScore);
        const searchResult = filteredScoredResultList.map((scoredResult) => scoredResult.hospital);
        setSearchResultList(searchResult);
    });
};
const useSkipCurrentPage = ({ careOrderBabyForm, fromPage, currentPage, navigate, }) => {
    useEffect(() => {
        if (fromPage === null)
            return;
        const hospitalName = get(careOrderBabyForm, 'hospitalObj.name');
        const hospitalCity = get(careOrderBabyForm, 'hospitalObj.city');
        const hospitalDistrict = get(careOrderBabyForm, 'hospitalObj.district');
        const shouldSkip = Boolean(hospitalName && hospitalCity && hospitalDistrict);
        if (!shouldSkip)
            return;
        handleSkipCurrentPage({
            fromPage,
            currentPage,
            navigate,
            nextPage: APP.ROUTES.HOSPITAL_CONFIRMATION,
            prevPage: APP.ROUTES.CONTRACT,
        });
    }, [fromPage]);
};
export default function Controller() {
    const { isLoading, setApiProcessingPool, setApiDonePool } = useApiLoadingPool(true);
    const [searchInputText, setSearchInputText] = useState('');
    const [searchResultPage, setSearchResultPage] = useState(1);
    const [searchResultList, setSearchResultList] = useState([]);
    const [selectedHospital, setSelectedHospital] = useState(null);
    const careOrderBabyForm = useSelector(selectCareOrderBabyForm);
    const hospitalList = useSelector(selectHospitalList);
    const hospitalOptionList = useMemo(() => getHospitalOptionList({
        hospitalList,
    }), [hospitalList]);
    const dispatch = useDispatch();
    const { navigate, useLocation } = useRouter();
    const location = useLocation();
    const currentPage = location.pathname;
    const { fromPage } = useTrackBrowserHistory();
    useSkipCurrentPage({ careOrderBabyForm, fromPage, currentPage, navigate });
    useGetRemoteConfig({ setApiProcessingPool, setApiDonePool });
    useGetCareClientVisitorProfile({ setApiProcessingPool, setApiDonePool });
    useValidateCareClientVisitorProfileAndRedirect({ redirectRoute: null });
    useScrollToTop();
    usePreventInfiniteLoading({ setApiProcessingPool, setApiDonePool });
    const { pageHeight } = usePageHeight();
    // event handlers
    const handleHospitalCardClick = useCallback(({ hospital }) => {
        setSelectedHospital({
            name: hospital.name,
            city: hospital.city,
            district: hospital.district,
        });
    }, []);
    const handleSearchInputChange = (e) => {
        const inputText = getStringWithoutSpecialCharacters({ string: e.target.value });
        flushSync(() => {
            setSearchInputText(inputText);
        });
        setSearchResultPage(1);
        searchHospital({
            queryText: inputText,
            setSelectedHospital,
            setSearchResultList,
            hospitalList,
        });
    };
    const handleLoadMoreButtonClick = useCallback(() => {
        setSearchResultPage((prev) => prev + 1);
    }, [setSearchResultPage]);
    const handleBackButtonClick = useCallback(() => {
        navigate(APP.ROUTES.CONTACT_INFO);
    }, []);
    const handleForwardButtonClick = useCallback(() => {
        if (!selectedHospital)
            return;
        dispatch(careOrderBabyFormUpdate({
            hospitalObj: selectedHospital,
        }));
        navigate(APP.ROUTES.HOSPITAL_CONFIRMATION);
    }, [selectedHospital]);
    // parsed data
    const totalPage = Math.ceil(hospitalOptionList.length / HOSPITAL_COUNT_PER_PAGE);
    const hospitalOptionDisplayList = hospitalOptionList.slice(0, searchResultPage * HOSPITAL_COUNT_PER_PAGE);
    const shouldRenderLoadMoreButton = searchResultPage < totalPage;
    const shouldShowSearchResult = searchInputText.length > 0;
    const hasNoSearchResult = Boolean(searchInputText && searchResultList.length === 0);
    const didCompleteRequiredFormFields = !!selectedHospital;
    return (<View 
    // data
    isLoading={isLoading} searchInputText={searchInputText} hospitalOptionDisplayList={hospitalOptionDisplayList} shouldRenderLoadMoreButton={shouldRenderLoadMoreButton} shouldShowSearchResult={shouldShowSearchResult} searchResultList={searchResultList} isForwardButtonAvailable={didCompleteRequiredFormFields} hasNoSearchResult={hasNoSearchResult} pageHeight={pageHeight} 
    // func
    getIsHospitalSelected={({ hospital, }) => getIsHospitalSelected({ hospital, selectedHospital })} handleHospitalCardClick={handleHospitalCardClick} handleSearchInputChange={handleSearchInputChange} handleLoadMoreButtonClick={handleLoadMoreButtonClick} handleBackButtonClick={handleBackButtonClick} handleForwardButtonClick={handleForwardButtonClick}/>);
}
